import React, { Component } from "react";
import moment from "moment";
import { GET_LIST } from "react-admin";
import { head, prop } from "ramda";
import dataProviderFactory from "../dataProvider";
import { estimateRentalFromBooking } from "../util/estimateRentalFromBooking";
import { buildRentalPromoSnapshot } from "../util/rentalPromo";

/**
 * Misma fuente y tramos que processRental.getItemsPrices:
 * /prices por modelo + GPS + insurance (for_date = price_date).
 * No usa booking.prices (puede traer seguro/tramo viejo).
 */
const priceListParams = (filter, priceDate) => ({
  filter: { ...filter, for_date: priceDate },
  pagination: { page: 1, perPage: 99999 },
  sort: { field: "start_date", order: "DESC" },
});

/** Tramo por días; si hay varias eras, gana start_date más reciente (como catálogo vigente). */
const vehicleDailyForDays = (modelPrices = [], days) => {
  const matches = (modelPrices || []).filter(
    price =>
      Number(price.days_interval_1) <= days &&
      Number(price.days_interval_2) >= days
  );
  if (!matches.length) return 0;
  matches.sort((a, b) => {
    const da = a.start_date ? new Date(a.start_date).getTime() : 0;
    const db = b.start_date ? new Date(b.start_date).getTime() : 0;
    return db - da;
  });
  return Number(prop("price", matches[0])) || 0;
};

class RentalCostSummary extends Component {
  state = {
    loading: true,
    modelPrices1: [],
    modelPrices2: [],
    gpsPrices: [],
    insurancePrices: [],
    error: null,
  };

  componentDidMount() {
    this.loadCatalog();
  }

  componentDidUpdate(prevProps) {
    const prev = prevProps.rental && prevProps.rental.booking;
    const next = this.props.rental && this.props.rental.booking;
    if (
      prev &&
      next &&
      (prev.price_date !== next.price_date ||
        prev.vehicle_model_1 !== next.vehicle_model_1 ||
        prev.vehicle_model_2 !== next.vehicle_model_2 ||
        prev.r_insurance_id !== next.r_insurance_id ||
        prev.r_insurance !== next.r_insurance)
    ) {
      this.loadCatalog();
    }
  }

  loadCatalog = async () => {
    const { rental } = this.props;
    if (!rental || !rental.booking) return;
    const booking = rental.booking;
    const priceDate = booking.price_date || new Date();
    const model1 = booking.vehicle_model_1;
    const model2 = booking.vehicle_model_2;

    this.setState({ loading: true, error: null });
    try {
      const dataProvider = await dataProviderFactory("rest");
      const reqs = [
        dataProvider(
          GET_LIST,
          "prices",
          priceListParams({ price_item_type: "insurance" }, priceDate)
        ),
        dataProvider(
          GET_LIST,
          "prices",
          priceListParams({ price_item_type: "gps_device" }, priceDate)
        ),
      ];
      if (model1) {
        reqs.push(
          dataProvider(
            GET_LIST,
            "prices",
            priceListParams({ prices_vehicle_model_id: model1 }, priceDate)
          )
        );
      }
      if (model2) {
        reqs.push(
          dataProvider(
            GET_LIST,
            "prices",
            priceListParams({ prices_vehicle_model_id: model2 }, priceDate)
          )
        );
      }
      const results = await Promise.all(reqs);
      const insurancePrices = results[0].data || [];
      const gpsPrices = results[1].data || [];
      let idx = 2;
      const modelPrices1 = model1 ? results[idx++].data || [] : [];
      const modelPrices2 = model2 ? results[idx++].data || [] : [];
      this.setState({
        loading: false,
        insurancePrices,
        gpsPrices,
        modelPrices1,
        modelPrices2,
      });
    } catch (e) {
      this.setState({ loading: false, error: e && e.message });
    }
  };

  renderLine = (label, est, strong) => {
    const { rental } = this.props;
    const dayActual = moment(Date.now()).format("YYYY-MM-DD");
    const showDeposit = moment(rental.date_1).diff(dayActual, "days") >= 30;
    const LabelTag = strong ? "strong" : "span";
    return (
      <React.Fragment>
        <label className="tamDetalle2">
          <LabelTag>{label}</LabelTag>
          {": "}
        </label>
        <span className="boldDetalle tamDetalle2">
          {showDeposit
            ? `${est.totalNet} €. (30% deposit: ${est.deposit30.toFixed(2)} € )`
            : `${est.totalNet} €.`}
        </span>
      </React.Fragment>
    );
  };

  render() {
    const { rental } = this.props;
    const {
      loading,
      error,
      modelPrices1,
      modelPrices2,
      gpsPrices,
      insurancePrices,
    } = this.state;
    if (!rental || !rental.booking) return null;
    if (loading) {
      return <span className="tamDetalle2">loading...</span>;
    }
    if (error) {
      return <span className="tamDetalle2">{`Error prices: ${error}`}</span>;
    }

    const booking = rental.booking;
    const promo = buildRentalPromoSnapshot({
      date_1: rental.date_1 || booking.r_date_1,
      time_1: rental.time_1 || booking.r_time_1,
      date_2: rental.date_2 || booking.r_date_2,
      time_2: rental.time_2 || booking.r_time_2,
      from_agency: rental.from_agency,
    });
    const motoDays = promo.moto_days || 1;
    const dailyMoto1 = vehicleDailyForDays(modelPrices1, motoDays);
    const dailyMoto2 = vehicleDailyForDays(modelPrices2, motoDays);
    const gpsRow = head(gpsPrices || []);
    const dailyGps = gpsRow && gpsRow.price != null ? Number(gpsRow.price) : 0;
    const locationFee =
      rental.location_1 &&
      rental.location_2 &&
      rental.location_1.id !== rental.location_2.id
        ? rental.location_1.id === 3 || rental.location_2.id === 3
          ? 375
          : 300
        : 0;

    const common = {
      booking,
      rental,
      dailyGps,
      insurancePrices,
      oneWayFee: locationFee,
      rewards: booking.rewards || 0,
    };
    const est1 = estimateRentalFromBooking({ ...common, dailyMoto: dailyMoto1 });
    const est2 = estimateRentalFromBooking({ ...common, dailyMoto: dailyMoto2 });

    return (
      <div>
        <div style={{ marginBottom: 8 }}>
          {this.renderLine("First choice motorcycle", est1, true)}
        </div>
        <div>{this.renderLine("Second choice motorcycle", est2, false)}</div>
       
      </div>
    );
  }
}

export default RentalCostSummary;



// WEBPACK FOOTER //
// ./src/components/RentalCostSummary.js